import { RefObject } from 'react';
import {
    Color,
    DoubleSide,
    type Material,
    Matrix4,
    MeshBasicMaterial,
    MeshPhysicalMaterial,
    Quaternion,
    Vector3
} from 'three';

import {
    PrefabModelType,
    ArticleInstancePlacementSourceType,
    ArticleModelLifeCycleCode,
    type ArticleInstancePlacement,
    type PartPlacement,
    type Point3D,
    type PrefabModel
} from 'api/models';
import { ASSET_REFERENCE_BASE_URL, EMPTY_GUID } from 'shared/constants';

import type { Article } from 'app/modules/wallforming/components/scenes/objects/articles/types/article';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';
import type { Part } from 'app/modules/wallforming/components/scenes/objects/articles/types/part';
import type { GltfAsset } from 'app/modules/wallforming/components/scenes/objects/helpers/types/gltfAsset';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

const DEBUG_SOURCE_TYPE_COLORS = ['true', '1', 'yes', 'on'].includes(
    String(import.meta.env.VITE_DEBUG_SOURCE_TYPE_COLORS ?? '')
        .trim()
        .toLowerCase()
);

/* Calculations */
export function getDistance3D(a: Vector3, b: { x: number; y: number; z: number }): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

const normalizeDegree = (deg: number) => {
    return ((deg % 360) + 360) % 360;
};

const normalizeRadiant = (rad: number) => {
    const twoPi = Math.PI * 2;
    return ((rad % twoPi) + twoPi) % twoPi;
};

export function convertToDegree(radians: number | undefined): number {
    return radians ? normalizeDegree((radians * 180) / Math.PI) : 0;
}

export function convertToRadiant(degrees: number | undefined): number {
    return degrees ? normalizeRadiant((degrees * Math.PI) / 180) : 0;
}

export function convertToFloatArray(point3dArray: Point3D[]) {
    const vertices = new Float32Array(point3dArray.length * 3);
    let i = 0;

    point3dArray.forEach((vertex) => {
        vertices[i] = vertex.x;
        vertices[i + 1] = vertex.y;
        vertices[i + 2] = vertex.z;
        i = i + 3;
    });

    return vertices;
}

const zUp = new Vector3(0, 0, 1);
const xUp = new Vector3(1, 0, 0);
const normalized = new Vector3();
const tmpVector = new Vector3();
const tangent = new Vector3();
const binormal = new Vector3();
const matrix = new Matrix4();
const alignmentQuaternion = new Quaternion();
const rollQuaternion = new Quaternion();

export function updateFullQuaternion(normal: Vector3, rotation: number, fullQuaternion: Quaternion): void {
    normalized.copy(normal).normalize();
    tmpVector.copy(Math.abs(normalized.dot(zUp)) > 0.99 ? xUp : zUp);

    tangent.crossVectors(tmpVector, normalized).normalize();
    binormal.crossVectors(normalized, tangent).normalize();
    matrix.makeBasis(tangent, binormal, normalized);

    alignmentQuaternion.setFromRotationMatrix(matrix);
    rollQuaternion.setFromAxisAngle(normalized, rotation);
    fullQuaternion.multiplyQuaternions(rollQuaternion, alignmentQuaternion);
}

export function calculateFullQuaternion(normal: Vector3, rotation: number): Quaternion {
    const fullQuaternion = new Quaternion();
    updateFullQuaternion(normal, rotation, fullQuaternion);
    return fullQuaternion;
}

export function calculateRotation(matrix: Matrix4, normal: Vector3) {
    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();
    matrix.decompose(position, quaternion, scale);

    const ref = Math.abs(normal.dot(new Vector3(0, 0, 1))) > 0.99 ? new Vector3(1, 0, 0) : new Vector3(0, 0, 1);
    const tangent = new Vector3().crossVectors(ref, normal.normalize()).normalize();
    const binormal = new Vector3().crossVectors(normal, tangent).normalize();
    const alignmentMatrix = new Matrix4().makeBasis(tangent, binormal, normal);
    const alignmentQuaternion = new Quaternion().setFromRotationMatrix(alignmentMatrix);

    const inverseAlignment = alignmentQuaternion.clone().invert();
    const rollQuaternion = new Quaternion().multiplyQuaternions(quaternion, inverseAlignment);

    let rotationAngle = 2 * Math.acos(rollQuaternion.w);
    const sinHalfAngle = Math.sqrt(1 - rollQuaternion.w * rollQuaternion.w);
    if (sinHalfAngle > 0.001) {
        const axis = new Vector3(
            rollQuaternion.x / sinHalfAngle,
            rollQuaternion.y / sinHalfAngle,
            rollQuaternion.z / sinHalfAngle
        );
        const dot = normal.dot(axis);
        rotationAngle *= dot > 0 ? 1 : -1;
    } else {
        rotationAngle = 0;
    }

    return rotationAngle;
}

/* Materials */
const holesMeshMaterial = new MeshBasicMaterial({
    depthWrite: true,
    side: DoubleSide,
    color: new Color('#666666'),
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -4
});

const LOD_SUFFIX_REGEX = /(?:[_\-\s]?lod[_\-\s]?([0-9]+)|[_\-\s]l([0-9]+))$/i;

export function normalizeMeshName(name: string): string {
    return name.replace(LOD_SUFFIX_REGEX, '').trim();
}

export function getMeshMaterial(name: string, color: Color = new Color('#ff0000')): Material {
    const normalizedName = normalizeMeshName(name).toLowerCase();

    if (normalizedName === 'holes') {
        return holesMeshMaterial;
    } else {
        return new MeshPhysicalMaterial({
            color,
            side: DoubleSide,
            transparent: true,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -4
        });
    }
}

/* GLTF */
export function createUrl(articleNumber: string, representationName: string, part?: string): string {
    return part && part !== ''
        ? `${ASSET_REFERENCE_BASE_URL}/glb/${articleNumber}-${representationName}-${part}.glb`
        : `${ASSET_REFERENCE_BASE_URL}/glb/${articleNumber}-${representationName}.glb`;
}

export function resolveUrl(
    articleNumber: string,
    representationName: string,
    assetReference: string | null | undefined,
    part?: string
): string {
    return assetReference && assetReference !== ''
        ? `${ASSET_REFERENCE_BASE_URL}/${assetReference}`
        : createUrl(articleNumber, representationName, part);
}

export function getGLTFUrls(articleNumber: string, representation: PrefabModel): GltfAsset[] {
    const urls: GltfAsset[] = [];

    if (representation.children && representation.children.length > 0) {
        representation.children.forEach((child) => {
            if (child.type === PrefabModelType.SubPart) {
                urls.push({
                    partName: child.name,
                    gltfUrl: resolveUrl(articleNumber, representation.name, child.assetReference, child.name)
                });
            }
        });
    } else {
        urls.push({
            partName: representation.name,
            gltfUrl: resolveUrl(articleNumber, representation.name, representation.assetReference)
        });
    }

    return urls;
}

/* Placement */
export function getArticleId(instanceId: string, state: PlacementState): string | undefined {
    for (const [articleId, article] of Object.entries(state.articles)) {
        if (instanceId in article.articleInstances) {
            return articleId;
        }
    }
    return undefined;
}

export function isRaycastEnabled(name: string): boolean {
    return normalizeMeshName(name).toLowerCase() !== 'holes';
}

const validLifeCycles: ArticleModelLifeCycleCode[] = [
    ArticleModelLifeCycleCode.I,
    ArticleModelLifeCycleCode.G,
    ArticleModelLifeCycleCode.P
];

export function isArticleForViewingOnly(lifeCycleCode: ArticleModelLifeCycleCode | undefined): boolean {
    return lifeCycleCode !== undefined && !validLifeCycles.includes(lifeCycleCode);
}

export function isPlacementIndicator(instanceId: string, state: PlacementState): boolean {
    if (!state.isAutoDragActiveRef.current) {
        return false;
    }

    const selectedInstanceIds = Object.keys(state.articleInstanceSelections).flat();
    return selectedInstanceIds.includes(instanceId);
}

export function getArticleInstanceData(
    eventInstanceId: number | undefined,
    articleInstances: Record<string, ArticleInstance>
) {
    return {
        index: eventInstanceId,
        articleInstanceId: eventInstanceId !== undefined ? Object.keys(articleInstances)[eventInstanceId] : undefined
    };
}

export function createInstanceParts(partKeys: string[] | undefined, basePart: Part): Record<string, Part> {
    if (!partKeys || partKeys?.length === 0) {
        return {};
    }

    const parts: Record<string, Part> = {};
    partKeys.forEach((partKey) => {
        parts[partKey] = clonePart(basePart);
    });

    return parts;
}

export function cloneArticleInstances(article: Article): Record<string, ArticleInstance> {
    return Object.fromEntries(
        Object.entries(article.articleInstances).map(([id, instance]) => [id, cloneArticleInstance(instance)])
    );
}

export function cloneArticleInstance(instance: ArticleInstance): ArticleInstance {
    return {
        parts: instance.parts ? cloneInstanceParts(instance.parts) : {},
        isLocked: instance.isLocked ?? false,
        poiGuids: instance.poiGuids ? [...instance.poiGuids] : [],
        sourceType: instance.sourceType,
        sourceGroupId: instance.sourceGroupId ?? null,
        sourceGroupName: instance.sourceGroupName ?? null,
        sourceGroupInstanceId: instance.sourceGroupInstanceId ?? null,
        sourceSavedGroupPrefabId: instance.sourceSavedGroupPrefabId ?? null
    };
}

export function cloneInstanceParts(parts: Record<string, Part>): Record<string, Part> {
    return Object.fromEntries(Object.entries(parts).map(([key, part]) => [key, clonePart(part)]));
}

export function clonePart(part: Part | undefined): Part {
    return {
        alignment: part?.alignment.clone() ?? new Vector3(0, 0, 1),
        position: part?.position.clone() ?? new Vector3(0, 0, 0),
        rotation: part?.rotation ?? 0,
        scale: part?.scale.clone() ?? new Vector3(1, 1, 1)
    };
}

const FRAMI_CLAMP_ARTICLE_NUMBER = '588433000';
const FRAMI_ADJUSTABLE_CLAMP_ARTICLE_NUMBER = '588436000';
const FRAMI_CLAMP_ARTICLE_NUMBERS = new Set([FRAMI_CLAMP_ARTICLE_NUMBER, FRAMI_ADJUSTABLE_CLAMP_ARTICLE_NUMBER]);
const SUPPORTING_CONSTRUCTION_ARTICLE_NUMBER = '588477000';
export const FRAMI_WEDGE_CLAMP_ARTICLE_NUMBER = '588441000';

export function isFramiClamp(articleNumber: string | null): boolean {
    return FRAMI_CLAMP_ARTICLE_NUMBERS.has((articleNumber ?? '').trim());
}

export function isSupportingConstruction(articleNumber: string | null): boolean {
    return (articleNumber ?? '').trim() === SUPPORTING_CONSTRUCTION_ARTICLE_NUMBER;
}

export function isFramiWedgeClamp(articleNumber: string | null): boolean {
    return (articleNumber ?? '').trim() === FRAMI_WEDGE_CLAMP_ARTICLE_NUMBER;
}

/** Articles placed through the anchor solver (supporting constructions and their Frami wedge clamps). */
export function isAnchorArticle(articleNumber: string | null): boolean {
    return isSupportingConstruction(articleNumber) || isFramiWedgeClamp(articleNumber);
}

export function isAdjustableFramiClamp(articleNumber: string | null): boolean {
    return (articleNumber ?? '').trim() === FRAMI_ADJUSTABLE_CLAMP_ARTICLE_NUMBER;
}

/* Mappings */
const indexToArticleLifeCycle = Object.values(ArticleModelLifeCycleCode) as ArticleModelLifeCycleCode[];

export function mapIndexToArticleLifeCycle(index: number | undefined): ArticleModelLifeCycleCode | undefined {
    if (index === undefined || index < 0 || index >= indexToArticleLifeCycle.length) {
        return undefined;
    }
    return indexToArticleLifeCycle[index];
}

export function mapArticleLifeCycleToIndex(lifeCycleCode: ArticleModelLifeCycleCode | undefined | null): number | null {
    if (lifeCycleCode === undefined || lifeCycleCode === null) {
        return null;
    }
    const index = indexToArticleLifeCycle.indexOf(lifeCycleCode);
    return index >= 0 ? index : null;
}

export function mapPartsArrayToRecord(parts: PartPlacement[]): Record<string, Part> {
    if (!parts || parts.length === 0) {
        return {};
    }

    const mapped: Record<string, Part> = {};

    parts.forEach((part) => {
        mapped[part.name] = {
            position: new Vector3(part.position.x, part.position.y, part.position.z),
            alignment: new Vector3(part.alignment.x, part.alignment.y, part.alignment.z),
            scale: new Vector3(part.scale.x, part.scale.y, part.scale.z),
            rotation: part.rotation
        };
    });

    return mapped;
}

export function mapPartsRecordToArray(instance: ArticleInstance): PartPlacement[] {
    if (!instance.parts) {
        return [];
    }

    const mapped: PartPlacement[] = Object.entries(instance.parts).map(([name, value]) => ({
        name,
        position: { x: value.position.x, y: value.position.y, z: value.position.z },
        alignment: { x: value.alignment.x, y: value.alignment.y, z: value.alignment.z },
        scale: { x: value.scale.x, y: value.scale.y, z: value.scale.z },
        rotation: value.rotation
    }));

    return mapped.length > 0 ? mapped : [];
}

export function mapPlacementToArticleInstance(instance: ArticleInstancePlacement): ArticleInstance {
    return {
        parts: mapPartsArrayToRecord(instance.parts),
        isLocked: instance.isLocked,
        poiGuids: [...new Set((instance.poiGuids ?? []).filter((poiGuid) => poiGuid && poiGuid !== EMPTY_GUID))],
        sourceType: instance.sourceType,
        sourceGroupId: instance.sourceGroupId,
        sourceGroupName: instance.sourceGroupName,
        sourceGroupInstanceId: instance.sourceGroupInstanceId,
        sourceSavedGroupPrefabId: instance.sourceSavedGroupPrefabId
    };
}

export function mapArticleInstanceToPlacement(
    articleInstanceId: string,
    instance: ArticleInstance,
    poiGuids: string[],
    isNewPlacement: boolean
): ArticleInstancePlacement {
    return {
        id: articleInstanceId,
        parts: mapPartsRecordToArray(instance),
        poiGuids,
        isNewPlacement,
        isLocked: instance.isLocked ?? false,
        sourceType: instance.sourceType,
        sourceGroupId: instance.sourceGroupId ?? null,
        sourceGroupName: instance.sourceGroupName ?? null,
        sourceGroupInstanceId: instance.sourceGroupInstanceId ?? null,
        sourceSavedGroupPrefabId: instance.sourceSavedGroupPrefabId ?? null
    };
}

export function getSourceTypeDebugColor(sourceType: ArticleInstancePlacementSourceType | undefined): Color | null {
    if (!DEBUG_SOURCE_TYPE_COLORS) {
        return null;
    }

    switch (sourceType) {
        case ArticleInstancePlacementSourceType.Panel:
            return new Color('#4b9efc');
        case ArticleInstancePlacementSourceType.UnsavedGroup:
            return new Color('#2fb97f');
        case ArticleInstancePlacementSourceType.SavedGroup:
            return new Color('#f39b36');
        case ArticleInstancePlacementSourceType.Gap:
            return new Color('#d24dff');
        default:
            return null;
    }
}

/* Placement Request */
export function findPoiGuidsByInstanceId(state: PlacementState, instanceId: string): string[] {
    for (const article of Object.values(state.articles)) {
        const poiGuids = article.articleInstances[instanceId]?.poiGuids;
        if (poiGuids?.length) {
            return [...poiGuids];
        }
    }

    for (const poi of state.pois) {
        for (const instanceIds of Object.values(poi.articles)) {
            if (instanceIds.includes(instanceId)) {
                return [poi.poi.poiGuid];
            }
        }
    }

    return [];
}

export function findPoiGuidByInstanceId(state: PlacementState, instanceId: string): string | null {
    return findPoiGuidsByInstanceId(state, instanceId)[0] ?? null;
}

export function buildGroupSavedPrefabId(state: PlacementState, groupId: string): string | null {
    const group = state.articleGroups[groupId];
    if (!group) {
        return null;
    }

    for (const member of group.members) {
        const article = state.articles[member.articleId];
        const instance = article?.articleInstances[member.instanceId];
        if (instance?.sourceSavedGroupPrefabId) {
            return instance.sourceSavedGroupPrefabId;
        }
    }

    return null;
}

/* Hover highlight */

export type HoverArticleGroupEntry = {
    articleId: string;
    ref: RefObject<ArticleReference | undefined>;
    instanceIds: string[];
};

export const hoveredGroupByArticle = new Map<string, HoverArticleGroupEntry>();
export const hoveredGroupInstanceIds = new Set<string>();

export function getHoverArticleRef(
    state: PlacementState,
    articleId?: string,
    articleInstanceId?: string
): RefObject<ArticleReference | undefined> | undefined {
    if (!articleId || !articleInstanceId) {
        return undefined;
    }

    const registeredRef = state.articleDragRefsMap.current.get(articleId)?.articleRef;
    if (registeredRef?.current?.articleInstances?.[articleInstanceId]) {
        return registeredRef;
    }

    const article = state.articles[articleId];
    const instance = article?.articleInstances[articleInstanceId];
    if (!article || !instance) {
        return undefined;
    }

    // Fallback keeps hover highlight stable while drag refs are rebuilding.
    return {
        current: {
            id: articleId,
            articleNumber: article.articleNumber,
            isMainCanvas: true,
            lifeCycleCode: article.lifeCycleCode,
            articleRepresentations: [],
            articleInstances: { [articleInstanceId]: instance },
            edgeGeometries: article.edgeGeometries
        }
    } as RefObject<ArticleReference | undefined>;
}

export function resolveHoverArticleRef(
    state: PlacementState,
    articleId: string,
    instanceId: string,
    articleRef: RefObject<ArticleReference | undefined>
): RefObject<ArticleReference | undefined> | undefined {
    if (articleRef.current?.id === articleId && articleRef.current.articleInstances?.[instanceId]) {
        return articleRef;
    }

    const registeredRef = state.articleDragRefsMap.current.get(articleId)?.articleRef;
    if (registeredRef?.current?.articleInstances?.[instanceId]) {
        return registeredRef;
    }

    return getHoverArticleRef(state, articleId, instanceId);
}

export function clearHoveredHighlight(state: PlacementState): void {
    if (hoveredGroupByArticle.size === 0) {
        state.setHoveredArticleInstanceIds([]);
        return;
    }

    for (const [articleId, entry] of hoveredGroupByArticle.entries()) {
        if (entry.instanceIds.length === 0) {
            continue;
        }

        const articleRef = getHoverArticleRef(state, articleId, entry.instanceIds[0]);
        state.updateInstanceMatrices(true, articleRef ?? entry.ref, false, entry.instanceIds);
    }

    hoveredGroupByArticle.clear();
    hoveredGroupInstanceIds.clear();
    state.setHoveredArticleInstanceIds([]);
}
